import { useCallback } from 'react';
import type { ReceivedDataMessage } from '@livekit/components-core';
import { useDataChannel } from '@livekit/components-react';
import { useQueryClient } from '@tanstack/react-query';

import { getGetOralExamCohortStateQueryKey } from '@/common/api/endpoints/oral-exams/oral-exams';

/** LiveKit data-channel topic for server-originated exam state doorbells. */
export const ORAL_EXAM_STATE_TOPIC = 'oral-exam-state';

type StateChangedPayload = {
  type: 'stateChanged';
  slotId: string;
  entityId: string;
};

function parseStateChangedPayload(payload: Uint8Array): StateChangedPayload | null {
  try {
    const parsed = JSON.parse(new TextDecoder().decode(payload)) as Partial<StateChangedPayload>;
    if (parsed?.type !== 'stateChanged' || !parsed.slotId || !parsed.entityId) {
      return null;
    }
    return {
      type: 'stateChanged',
      slotId: parsed.slotId,
      entityId: parsed.entityId,
    };
  } catch {
    return null;
  }
}

function runStateQueryKeySuffix(entityId: string): string {
  return `/bookings/${entityId}/state`;
}

/**
 * OralExamStateDoorbell
 * ----
 * Listens for server doorbells on the shared exam LiveKit room and invalidates
 * cohort roster + booking run-state queries so clients re-read without waiting
 * for the safety poll.
 */
export function OralExamStateDoorbell() {
  const queryClient = useQueryClient();

  const onStateDoorbell = useCallback((msg: ReceivedDataMessage) => {
    if (msg.from) return;

    const payload = parseStateChangedPayload(msg.payload);
    if (!payload) return;

    queryClient.invalidateQueries({
      queryKey: getGetOralExamCohortStateQueryKey(payload.slotId),
    });

    const runStateSuffix = runStateQueryKeySuffix(payload.entityId);
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        if (!Array.isArray(key) || key.length === 0) return false;
        const first = key[0];
        return typeof first === 'string' && first.endsWith(runStateSuffix);
      },
    });
  }, [queryClient]);

  useDataChannel(ORAL_EXAM_STATE_TOPIC, onStateDoorbell);

  return null;
}
